// PatientSearchBar.tsx
import React, { useRef, useState } from "react";
import { Toolbar, TextField, Autocomplete, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import useStore from "../../helpers/useStore";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import {
  usePatientLocationSearch,
  PatientSearchOption,
} from "../../stores/Patients/patient.search";
import {
  renderPatientSearchGroup,
  renderPatientSearchOption,
} from "../../molecules/PatientSearchOption";
import { BaseResultDtoKindEnum } from "../../api-client";

interface PatientSearchBarProps {
  style?: React.CSSProperties;
  limit?: number; // default page size for suggestions
}

const PatientSearchBar: React.FC<PatientSearchBarProps> = observer(({ style, limit = 100 }) => {
  const patientsStore = useStore(PatientsContext);
  const { handleOpenPatient } = PatientCommands(patientsStore);
  const navigate = useNavigate();

  const [input, setInput] = useState("");
  const [selectedValue, setSelectedValue] = useState<PatientSearchOption | null>(null); // control selected option so we can reset it

  const inputRef = useRef<HTMLInputElement | null>(null); // to blur after selection

  const { options, loading } = usePatientLocationSearch(input, { limit });

  // Simple selection handler - no need for useCallback with React Compiler
  function handleSelect(_: any, value: PatientSearchOption | null) {
    if (!value || value.isEmptyPlaceholder) return;

    // Extract patientId and rxId from the selected item
    let patientId: number;
    let rxId: string | undefined;

    if (value.kind === BaseResultDtoKindEnum.Patient) {
      patientId = Number(value.patientId ?? value.id);
      rxId = undefined;
    } else if (value.kind === BaseResultDtoKindEnum.Prescription) {
      patientId = Number(value.patientId ?? value.id);
      rxId = value.prescriptionId;
    } else {
      return; // Unknown kind
    }

    // Handle MobX state changes via command
    handleOpenPatient(patientId, rxId);

    // Reset Autocomplete controlled states
    setSelectedValue(null); // clear the selected option so the chip/value disappears
    setInput(""); // clear visible text

    // Blur after current event & then navigate (timeout ensures DOM update)
    setTimeout(() => {
      inputRef.current?.blur();
      navigate("/patient");
    }, 0);
  }

  return (
    <Toolbar sx={{ width: "30%", minHeight: 0, p: 0 }}>
      <Autocomplete<PatientSearchOption, false, false, false>
        value={selectedValue}
        inputValue={input}
        options={options}
        loading={loading}
        // The backend already filtered and ranked by location - re-filtering
        // client side would drop valid hits (e.g. "Müller Anna" never matches
        // the "Müller, Anna • …" label).
        filterOptions={(o) => o}
        groupBy={(o) => o.group}
        getOptionLabel={(o) => o.label}
        getOptionDisabled={(o) => !!o.isEmptyPlaceholder}
        onChange={handleSelect}
        onInputChange={(_, v, reason) => {
          setInput(v);
          if (reason === "clear") {
            setSelectedValue(null);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            // Removed label to prevent MUI floating label animation; using placeholder only
            placeholder="Patienten / Rezepte suchen"
            inputRef={inputRef}
            InputLabelProps={{ shrink: false }}
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Patienten / Rezepte suchen',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              width: "100%",
              borderRadius: "5px",
              '& .MuiOutlinedInput-root': { height: "50px", px: 1 },
              // Defensive: ensure any residual label styles are neutralized
              '& .MuiInputLabel-root': { display: 'none' },
            }}
          />
        )}
        renderGroup={renderPatientSearchGroup}
        renderOption={renderPatientSearchOption}
        sx={{
          width: "100%",
          backgroundColor: "transparent !important",
          "& .MuiAutocomplete-root": {
            backgroundColor: "transparent !important",
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: "transparent !important",
          },
          "& .MuiInputBase-root": {
            backgroundColor: "transparent !important",
          },
          ...style,
        }}
      />
    </Toolbar>
  );
});

export default PatientSearchBar;
